/**
 * NutritionFoodCard — single food row inside MealTimelineCard.
 *
 * Display mode: name (truncated, full text on title), qty + unit
 * top-right, MacroBadge underneath.
 *
 * Edit mode: name still rendered (truncated, NOT editable inline per
 * VH-82), quantity number input (≥ 60 px), unit label, optional
 * re-analyze button, delete button (44 px touch target). The four
 * macro values (kcal / P / C / F) are inline number inputs underneath
 * so the user can override them manually — the ✨ re-analyze button
 * stays as a shortcut to AI-recompute from the new quantity.
 */
import { TrashIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import MacroBadge from '../../shared/MacroBadge'

export default function NutritionFoodCard({
  food,
  displayName,
  editable = false,
  isReanalyzing = false,
  onQuantityChange,
  onCaloriesChange,
  onProteinChange,
  onCarbsChange,
  onFatChange,
  onDelete,
  onReanalyze,
}) {
  const { t } = useTranslation()

  const calories = food.calories_contributed ?? food.calories ?? 0
  const protein = food.protein_contributed ?? food.protein ?? 0
  const carbs = food.carbs_contributed ?? food.carbs ?? 0
  const fat = food.fat_contributed ?? food.fat ?? 0

  const qty = food.quantity ?? 0
  const unit = food.quantity_unit || 'g'

  const isNew = !!food.isNew

  return (
    <div
      className={`border rounded-lg p-3 ${isNew ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-surface-container'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="font-medium text-gray-900 truncate text-base flex-1 min-w-0"
          title={displayName}
        >
          {displayName}
        </span>

        {editable ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <input
              type="number"
              value={qty}
              min="0"
              step="0.1"
              onChange={(e) =>
                onQuantityChange?.(parseFloat(e.target.value) || 0)
              }
              className="w-16 min-w-[60px] px-2 py-1 text-sm text-right tabular-nums"
              aria-label={t('nutrition.qty', 'Quantity')}
            />
            <span className="text-sm text-gray-500">{unit}</span>
            {onReanalyze && (
              <button
                type="button"
                onClick={onReanalyze}
                disabled={isReanalyzing}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-indigo-500 hover:bg-indigo-50 rounded disabled:opacity-50"
                title={t('nutrition.edit.reanalyze', 'Recalculate nutrition')}
              >
                {isReanalyzing ? (
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <SparklesIcon className="h-4 w-4" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onDelete}
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-red-500 hover:bg-red-50 rounded"
              aria-label={t('common.delete', 'Delete')}
              title={t('common.delete', 'Delete')}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap flex-shrink-0">
            {qty}
            {unit}
          </span>
        )}
      </div>

      {editable ? (
        <div
          className="mt-2 grid grid-cols-4 gap-2"
          data-testid="nutrition-food-card-macros-edit"
        >
          <MacroInput
            value={calories}
            onChange={onCaloriesChange}
            label={t('nutrition.macros.short.calories', 'kcal')}
            ariaLabel={t('nutrition.macros.calories', 'Calories')}
            testid="macro-calories-input"
            step="1"
          />
          <MacroInput
            value={protein}
            onChange={onProteinChange}
            label={`${t('nutrition.macros.short.protein', 'P')} g`}
            ariaLabel={t('nutrition.macros.protein', 'Protein')}
            testid="macro-protein-input"
            step="0.1"
          />
          <MacroInput
            value={carbs}
            onChange={onCarbsChange}
            label={`${t('nutrition.macros.short.carbs', 'C')} g`}
            ariaLabel={t('nutrition.macros.carbs', 'Carbs')}
            testid="macro-carbs-input"
            step="0.1"
          />
          <MacroInput
            value={fat}
            onChange={onFatChange}
            label={`${t('nutrition.macros.short.fat', 'F')} g`}
            ariaLabel={t('nutrition.macros.fat', 'Fat')}
            testid="macro-fat-input"
            step="0.1"
          />
        </div>
      ) : (
        <div className="mt-2">
          <MacroBadge
            calories={calories}
            protein={protein}
            carbs={carbs}
            fat={fat}
            compact
          />
        </div>
      )}
    </div>
  )
}

function MacroInput({ value, onChange, label, ariaLabel, testid, step }) {
  return (
    <label className="flex items-center gap-1 text-xs text-gray-600">
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange?.(parseFloat(e.target.value) || 0)}
        className="w-full min-w-0 px-1.5 py-1 text-sm text-right tabular-nums"
        aria-label={ariaLabel}
        data-testid={testid}
      />
      <span className="shrink-0 text-gray-500">{label}</span>
    </label>
  )
}
